import { differenceInCalendarDays } from 'date-fns'

// Schemas
import { notification as notificationSchema, notificationList } from 'normalize'

// Selectors
import { selectRevenueReportingStatus } from 'modules/usersongs/selectors'

// Types
import { GetState, Dispatch, APIAction, Notification } from 'types'

// Utils
import { safeLocalStorage as localStorage } from 'utils'
import formatDate from 'lib/formatdate'

export const PERSIST_NORMAL = 1
export const PERSIST_ALWAYS = 2
export const PERSIST_FLASH = 3

const fourHoursInMs = 14400000

export const actions = {
  fetch: () => ({
    CALL_API: {
      type: 'FETCH_NOTIFICATIONS',
      method: 'GET',
      endpoint: '/api/notification/',
      schema: notificationList,
    },
  }),

  markAsSeen: (notification: Notification) => (dispatch: Dispatch) => {
    if (notification.localId) {
      dispatch(actions.seenLocal(notification))
    } else if (notification.id) {
      dispatch(actions.seenRemote(notification.id))
    }
  },

  seenLocal: (notification: Notification) => ({
    type: 'NOTIFICATION_REMOVE_LOCAL',
    notification,
  }),

  seenRemote: (notificationId: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_NOTIFICATIONS',
      method: 'PATCH',
      endpoint: `/api/notification/${notificationId}/`,
      schema: notificationSchema,
      data: { seen: true },
    },
  }),

  createError: (title?: string, message?: string) => (dispatch: Dispatch) => {
    return dispatch(
      actions.createLocal({
        title: title || 'Something went wrong!',
        body: message || 'Please try again or contact support',
        datatesting: 'notification_created_error',
      }),
    )
  },

  createUserSongCreatedNotification: (userSongId: number, songId: number) => (dispatch: Dispatch, getState: GetState) => {
    const userSong = getState().entities.userSongs[String(userSongId)]
    const song = getState().entities.songs[String(songId)]

    return dispatch(
      actions.createLocal({
        title: `User song "${userSong.song_title}" created`,
        body: `A user song with samples from ${song.title} has been created!`,
        cover: song.cover,
        persistence: PERSIST_FLASH,
        icon_url: 'cover',
        button_url: `/yoursongs/${userSongId}/sample/`,
        button_text: 'Go to user song page',
        datatesting: 'notification_createed_usersong',
      }),
    )
  },

  createUserSongSentNotification: (userSongId: number, email: string) => (dispatch: Dispatch, getState: GetState) => {
    const userSong = getState().entities.userSongs[String(userSongId)]
    const songId = userSong.samples[0].song
    const song = getState().entities.songs[String(songId)]

    return dispatch(
      actions.createLocal({
        title: `User song "${userSong.song_title}" sent!`,
        body: `Your song has been sent to ${email}`,
        cover: song.cover,
        persistence: PERSIST_FLASH,
        icon_url: 'cover',
      }),
    )
  },

  createUserSongReceivedNotification: (userSongId: number) => (dispatch: Dispatch, getState: GetState) => {
    const userSong = getState().entities.userSongs[String(userSongId)]
    const songId = userSong.samples[0].song
    const song = getState().entities.songs[String(songId)]

    return dispatch(
      actions.createLocal({
        title: `User song "${userSong.song_title}" received!`,
        body: `User song "${userSong.song_title}" has been added to your songs`,
        cover: song.cover,
        persistence: PERSIST_FLASH,
        icon_url: 'cover',
      }),
    )
  },

  createUserSongDeletedNotification: (userSongId: number) => (dispatch: Dispatch, getState: GetState) => {
    const userSong = getState().entities.userSongs[String(userSongId)]

    dispatch(
      actions.createLocal({
        title: `User song "${userSong.song_title}" deleted`,
        body: 'Your usersong has been deleted!',
        persistence: PERSIST_FLASH,
      }),
    )
  },

  createTrackAddedToCollectionNotification:
    (trackId: number, collectionId: number, loop?: boolean) => (dispatch: Dispatch, getState: GetState) => {
      const { entities, router } = getState()
      const track = entities.tracks[String(trackId)]
      const song = entities.songs[String(track.song)]
      const collection = entities.collections[String(collectionId)]
      const { profiles } = entities
      const profilesList = Object.keys(profiles).map(id => profiles[id])
      const favoritesCollection = profilesList[0].favorites_collection_id
      const isOnboarding = router.location.pathname.startsWith('/onboard')

      if (collection?.id === favoritesCollection) {
        return dispatch(
          actions.createLocal({
            title: `${loop ? 'Loop' : 'Track'} added to favorites`,
            body: `${song.title} - ${track.name} added to your favorites`,
            cover: song.cover,
            icon_url: 'cover',
            collection,
            persistence: PERSIST_FLASH,
            button_url: isOnboarding ? null : '/favorites',
            button_text: isOnboarding ? null : 'Go to your favorites',
          }),
        )
      }

      return dispatch(
        actions.createLocal({
          title: `${loop ? 'Loop' : 'Track'} added to collection`,
          body: `${song.title} - ${track.name} added to collection ${collection.title}`,
          cover: song.cover,
          icon_url: 'cover',
          collection,
          persistence: PERSIST_FLASH,
          button_url: `/collection/${collectionId}`,
          button_text: 'Go to collection',
        }),
      )
    },

  createLocalReferralsReminder: () => (dispatch: Dispatch, getState: GetState) => {
    const latestReferralReminder = 'latestReferralReminder'
    const lastReferralsReminder = localStorage.getItem(latestReferralReminder)
    const notification = {
      title: 'Get 10 free credits by inviting your friends to Tracklib!',
      body: 'For every friend who becomes a paying subscriber, we’ll give you 10 free tracks - and your friend 30 days free, with 15 tracks!',
      persistence: PERSIST_NORMAL,
      icon_url: 'referral',
      button_url: '/referrals/',
      button_text: 'Invite your friends',
    }
    const state = getState()
    const { profileId } = state.auth
    const profile = state.entities.profiles[String(profileId)]

    const now = new Date()
    const signupDate = new Date(profile.date_joined)
    const hasBeenAtLeastOneDaySinceSignup = differenceInCalendarDays(now.getTime(), signupDate.getTime()) > 0
    if (!lastReferralsReminder && hasBeenAtLeastOneDaySinceSignup) {
      localStorage.setItem(latestReferralReminder, String(new Date()))
      return dispatch(actions.createLocal(notification))
    }
    return Promise.resolve()
  },

  createLocalVerificationReminder: () => (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const { profileId } = state.auth
    const profile = state.entities.profiles[String(profileId)]
    const isVerified = profile?.emails?.some(x => x.verified)
    const latestVerificationReminder = localStorage.getItem('latestVerificationReminder')

    const notification = {
      title: 'Please verify your email',
      body: 'Click the link in the email we sent to verify your email address.',
      persistence: PERSIST_NORMAL,
    }

    if (!latestVerificationReminder && !isVerified) {
      localStorage.setItem('latestVerificationReminder', String(new Date()))
      return dispatch(actions.createLocal(notification))
    }

    const lcrDate = new Date(latestVerificationReminder)
    const now = new Date()

    if (now.getTime() - lcrDate.getTime() > fourHoursInMs && !isVerified) {
      localStorage.setItem('latestVerificationReminder', String(new Date()))
      return dispatch(actions.createLocal(notification))
    }
    return Promise.resolve()
  },

  createLocalRevenueReportingReminder: () => (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const { pathname } = state.router.location
    const isOnRevenueReportingPage = pathname.endsWith('/revenuereporting/')

    const revenueReportingStatus = selectRevenueReportingStatus(state)
    const { reportingOpen, reportingNeeded, paymentNeeded, dueDate } = revenueReportingStatus
    const timeToReport = reportingNeeded && !paymentNeeded
    const unpaidRevenue = !reportingNeeded && paymentNeeded
    const canMakeChanges = !reportingNeeded && !paymentNeeded
    if (reportingOpen && (timeToReport || unpaidRevenue || canMakeChanges) && !isOnRevenueReportingPage) {
      const latestRevenueReportingReminder = localStorage.getItem('latestRevenueReportingReminder')

      const send = () => {
        localStorage.setItem('latestRevenueReportingReminder', String(new Date()))
        if (timeToReport) {
          return dispatch(
            actions.createLocal({
              title: "It's time to report your revenue for all your active licenses",
              body: `Revenue report is due ${dueDate ? formatDate(dueDate) : ''}`,
              persistence: PERSIST_NORMAL,
              button_url: '/yoursongs/revenuereporting/',
              button_text: 'Report Revenue',
              icon_url: 'report-income',
            }),
          )
        }
        if (unpaidRevenue) {
          return dispatch(
            actions.createLocal({
              title: 'You have unpaid royalties',
              body: `Payment is due ${dueDate ? formatDate(dueDate) : ''}`,
              persistence: PERSIST_NORMAL,
              button_url: '/yoursongs/revenuereporting/',
              button_text: 'Pay royalties',
              icon_url: 'report-income',
            }),
          )
        }
        if (canMakeChanges) {
          return dispatch(
            actions.createLocal({
              title: 'You can still make changes to your revenue report',
              body: `Reporting closes on ${dueDate ? formatDate(dueDate) : ''}`,
              persistence: PERSIST_NORMAL,
              button_url: '/yoursongs/revenuereporting/',
              button_text: 'Report revenue',
              icon_url: 'report-income',
            }),
          )
        }
        return Promise.resolve()
      }

      if (!latestRevenueReportingReminder) {
        return send()
      }

      const lcrDate = new Date(latestRevenueReportingReminder || '')
      const now = new Date()

      if (now.getTime() - lcrDate.getTime() > fourHoursInMs) {
        return send()
      }
    }
    return Promise.resolve()
  },

  downloadStarting: () => (dispatch: Dispatch) =>
    dispatch(
      actions.createLocal({
        title: 'Preparing for download',
        body: 'You can continue using the site as usual, your download will start automatically when ready.',
        persistence: PERSIST_NORMAL,
        icon_url: 'download',
      }),
    ),

  downloadError: () => (dispatch: Dispatch) =>
    dispatch(
      actions.createLocal({
        title: 'Download error',
        body: 'Something is not working properly. Contact support for assistance.',
        persistence: PERSIST_NORMAL,
        icon_url: 'download',
      }),
    ),

  discountNotifications: () => (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const subscription = Object.values(state.entities.subscriptions)[0]

    const freeTierNotification = {
      title: '50% OFF ALL PLANS',
      body: 'Anniversary offer! Just now - 50% off all plans for the first 6 months. Sign up for $2.99/month!',
      persistence: PERSIST_NORMAL,
      button_url: '/settings/?modal=changeplan',
      button_text: 'Sign up now',
      icon_url: 'smiley',
    }

    const monthlyTierNotification = {
      title: '50% OFF YEARLY UPGRADE PLANS',
      body: 'It’s time to celebrate! Just now - 50% off when upgrading to a yearly plan. From $29.90 for a full year.',
      persistence: PERSIST_NORMAL,
      button_url: '/settings/?modal=changeplan',
      button_text: 'Sign up now',
      icon_url: 'smiley',
    }

    if (subscription?.state === 'FREE') {
      return dispatch(actions.createLocal(freeTierNotification))
    }

    if (subscription?.renewal_period?.months === 1) {
      return dispatch(actions.createLocal(monthlyTierNotification))
    }
    return Promise.resolve()
  },

  createLocal: (data: Record<string, unknown>) => (dispatch: Dispatch, getState: GetState) => {
    const localId = Object.keys(getState().notifications.entities).length + 1
    return dispatch({
      type: 'CREATE_NOTIFICATION_LOCAL',
      data: { ...data, localId },
    })
  },

  fetchLocalReminders: () => (dispatch: Dispatch) => {
    dispatch(actions.fetch())
    dispatch(actions.createLocalReferralsReminder())
    dispatch(actions.discountNotifications())
    return dispatch(actions.createLocalVerificationReminder())
  },
}
